import { useQuery } from '@apollo/client';
import { T1, ALL_MODULES } from '@nile/components';
import { GET_FIELD, AccountClient, SystrayAccountClient } from '@nile/graphql';
import { DisabledTag } from '@nile/mangifera/src/Pebbles/TicketContainer';
import {
  Checkbox,
  Popover,
  Button,
  ButtonGroup
} from '@superops/ui-design-system';
import { Edit } from '@styled-icons/material/Edit';
import { cloneDeep, isEqual } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { EditIcon } from '@nile/components/src/Icons';

const CustomField = styled.div`
  min-width: 200px;
  max-width: ${(props) => (props?.fullWidth ? 'unset' : '250px')};
  flex-grow: 1;
  padding-right: ${(props) => !props?.fullWidth && '10px'};
  padding-left: ${(props) => !props?.fullWidth && '10px'};
  margin-top: 8px;
  margin-bottom: 8px;
  .Polaris-ButtonGroup {
    margin-top: 12px;
  }
`;

const Label = styled.div`
  text-align: left;
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 10px;
`;

const Wrap = styled.div`
  display: flex;
  align-items: center;
  .edit-actions {
    visibility: hidden;
    cursor: pointer;
  }
  padding-left: 8px;
  padding-right: 8px;
  max-width: 100%;
`;

const CField = styled.div`
  .editable-cell,
  .cell {
    flex-grow: 1;
    font-weight: bold;
  }
  &:hover {
    .edit-actions {
      visibility: visible;
    }
  }
`;

function AsyncCheckboxNew(props) {
  const {
    field,
    ticket,
    defaultValue,
    updateSelection,
    editable,
    value,
    inlineEdit,
    disableSelection,
    isMandatory,
    systray = false
  } = props;
  const { data, loading, error } = useQuery(GET_FIELD, {
    client: systray ? SystrayAccountClient : AccountClient,
    variables: {
      input: {
        columnName: field.columnName || field.name,
        module: props.module || ALL_MODULES.ticket
      }
    }
  });
  const [selectedItems, setSelectedItems] = useState(value || []);
  const [popoverActive, setPopoverActive] = useState(false);
  const togglePopoverActive = useCallback(() => {
    setPopoverActive(!popoverActive);
  });
  const [updatedValue, setUpdatedValue] = useState(value || []);
  const purgeEmptyStrings = (arr) => {
    if (arr) {
      const clonedArr = cloneDeep(arr);
      arr.filter((val, i) => {
        if (val === '') {
          clonedArr.splice(i, 1);
        }
      });
      return clonedArr;
    }
    return [];
  };
  useEffect(() => {
    if (!isEqual(updatedValue, value)) {
      if (!value) {
        setUpdatedValue([]);
      } else {
        setUpdatedValue(value);
      }
    }
  }, [value]);
  const updateUpdatedValue = (checked, newValue) => {
    const allValues = purgeEmptyStrings(cloneDeep(updatedValue));
    if (!checked) {
      const index = allValues.indexOf(newValue);
      allValues.splice(index, 1);
      if (allValues.length) {
        setUpdatedValue(allValues);
      } else {
        setUpdatedValue(null);
      }
    } else {
      allValues.push(newValue);
      setUpdatedValue(allValues);
    }
  };
  const updateSelectedField = (checked, newValue) => {
    const allValues = purgeEmptyStrings(cloneDeep(selectedItems));
    if (!checked) {
      const index = allValues.indexOf(newValue);
      allValues.splice(index, 1);
      if (allValues.length) {
        setSelectedItems(allValues);
      } else {
        setSelectedItems(null);
      }
    } else {
      allValues.push(newValue);
      setSelectedItems(allValues);
    }
    if (allValues.length) {
      updateSelection(allValues);
    } else {
      updateSelection(null);
    }
  };
  if (loading) return <div>loading...</div>;
  const displayVal = value;
  const activator = <EditIcon onClick={() => togglePopoverActive()} />;
  const fldOptions =
    data?.getField?.options || data?.getField?.fieldOptions || [];
  const getTag = () => {
    return (
      <CustomField className="cf-wrapper">
        <Label className="cf-label">
          {field.label || ''}{' '}
          {isMandatory && (
            <>
              <span className="mandatory-star">*</span>
            </>
          )}
        </Label>
        <div
          className={`flex col ${props.error ? 'cf-error-state' : ''}`}
          key={field.name}
        >
          {fldOptions.map((item) => {
            return (
              <Checkbox
                label={item.value}
                id={`checkbox-${item.value}-${field.columnName || field.name}`}
                name={`checkbox-${item.value}-${field.columnName ||
                  field.name}`}
                checked={updatedValue?.indexOf(item.value) > -1}
                onChange={(checked) => updateUpdatedValue(checked, item.value)}
                disabled={disableSelection}
              />
            );
          })}
        </div>
        <div className="mt10">
          <ButtonGroup>
            <Button
              onClick={() => {
                togglePopoverActive();
                setUpdatedValue(value || []);
              }}
            >
              <FormattedMessage id="common.cancel" />
            </Button>
            <Button
              primary
              onClick={() => {
                updateSelection(updatedValue);
                togglePopoverActive();
              }}
            >
              <FormattedMessage id="common.save" />
            </Button>
          </ButtonGroup>
        </div>
      </CustomField>
    );
  };

  if (inlineEdit) {
    return !popoverActive ? (
      <CField>
        <Wrap className={`${editable ? 'editable-cell' : 'cell'}`}>
          <div className="ellipsis" title={displayVal}>
            {displayVal?.length ? (
              displayVal.join(',')
            ) : (
              <DisabledTag>NA</DisabledTag>
            )}
          </div>
          {editable && <div className="edit-actions">{activator}</div>}
        </Wrap>
      </CField>
    ) : (
      <CField>
        <Wrap className="editable-cell">
          <div className="ellipsis" title={displayVal}>
            {displayVal?.length ? (
              displayVal.join(',')
            ) : (
              <DisabledTag>NA</DisabledTag>
            )}
          </div>
          <Popover
            activator={activator}
            active={popoverActive}
            onClose={() => {
              // togglePopoverActive()
            }}
          >
            {getTag()}
          </Popover>
        </Wrap>
      </CField>
    );
  }
  return (
    <CustomField fullWidth={props?.fullWidth} className="cf-wrapper">
      <Label className="cf-label">
        {field.label || ''}
        {isMandatory && (
          <>
            <span className="mandatory-star">*</span>
          </>
        )}
      </Label>
      <div
        className={`flex col ${props.error ? 'cf-error-state' : ''}`}
        key={field.name}
      >
        {fldOptions.map((item) => {
          // TODO: ASH: Check this part
          return (
            <Checkbox
              label={item.value}
              id={`checkbox-${item.value}-${data?.getField.columnName}`}
              name={`checkbox-${item.value}-${data?.getField.columnName}`}
              checked={selectedItems?.indexOf(item.value) > -1}
              onChange={(checked) => updateSelectedField(checked, item.value)}
              disabled={disableSelection}
            />
          );
        })}
      </div>
    </CustomField>
  );
}

export default AsyncCheckboxNew;
